import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { listFiles, deleteFile, humanBytes, fileBlobUrl } from "@/lib/files";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  CheckCircle2, Circle, Loader2, Plus, Trash2, MessageSquare, Paperclip,
  ListChecks, Download, Send, Pin, History, CalendarClock, SkipForward, Eye,
} from "lucide-react";
import FilePreviewDialog from "@/components/FilePreviewDialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import FileDropZone from "@/components/FileDropZone";

const PRIO = ["low", "normal", "high", "urgent"];
const STATUS = ["todo", "in_progress", "done", "missed"];

export default function TaskDrawer({ taskId, open, onClose, stages = [], onChanged }) {
  const [task, setTask] = useState(null);
  const [files, setFiles] = useState([]);
  const [people, setPeople] = useState([]);
  const { settings } = useSettings() || {};
  const [previewFile, setPreviewFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newSub, setNewSub] = useState("");
  const [newComment, setNewComment] = useState("");
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const [{ data }, f] = await Promise.all([api.get(`/tasks/${taskId}`), listFiles({ task_id: taskId })]);
      setTask(data);
      setFiles(f);
    } catch { toast.error("Could not load task"); }
    finally { setLoading(false); }
  }, [taskId]);

  useEffect(() => { if (open && taskId) load(); }, [open, taskId, load]);
  useEffect(() => {
    if (!open || people.length) return;
    api.get("/people").then(({ data }) => setPeople(data)).catch((err) => console.error("Could not load people", err));
  }, [open, people.length]);
  useEffect(() => () => clearTimeout(timer.current), []);

  const patch = async (body, { silent = false } = {}) => {
    setTask((t) => ({ ...t, ...body }));
    setSaving(true);
    try {
      const { data } = await api.put(`/tasks/${taskId}`, body);
      setTask(data);
      onChanged?.(data);
      if (!silent) toast.success("Saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const patchDebounced = (body) => {
    setTask((t) => ({ ...t, ...body }));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => patch(body, { silent: true }), 700);
  };

  const addSubtask = async () => {
    const title = newSub.trim();
    if (!title) return;
    setNewSub("");
    try {
      const { data } = await api.post(`/tasks/${taskId}/subtasks`, { title });
      setTask(data); onChanged?.(data);
    } catch { toast.error("Could not add subtask"); }
  };

  const toggleSubtask = async (st) => {
    setTask((t) => ({ ...t, subtasks: t.subtasks.map((s) => (s.id === st.id ? { ...s, done: !s.done } : s)) }));
    try {
      const { data } = await api.put(`/tasks/${taskId}/subtasks/${st.id}`, { done: !st.done });
      const allDone = (data.subtasks || []).length > 0 && data.subtasks.every((s) => s.done);
      if (allDone && data.status !== "done") {
        const { data: finished } = await api.put(`/tasks/${taskId}`, { status: "done" });
        toast.success("All subtasks done — task complete");
        setTask(finished); onChanged?.(finished);
        return;
      }
      setTask(data); onChanged?.(data);
    } catch { toast.error("Update failed"); load(); }
  };

  const removeSubtask = async (st) => {
    try {
      const { data } = await api.delete(`/tasks/${taskId}/subtasks/${st.id}`);
      setTask(data); onChanged?.(data);
    } catch { toast.error("Delete failed"); }
  };

  const addComment = async () => {
    const body = newComment.trim();
    if (!body) return;
    setNewComment("");
    try {
      const { data } = await api.post(`/tasks/${taskId}/comments`, { body });
      setTask(data); onChanged?.(data);
    } catch { toast.error("Could not post comment"); }
  };

  const removeComment = async (c) => {
    try {
      const { data } = await api.delete(`/tasks/${taskId}/comments/${c.id}`);
      setTask(data); onChanged?.(data);
    } catch { toast.error("Delete failed"); }
  };

  const removeTask = async () => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await api.delete(`/tasks/${taskId}`);
      toast.success("Task deleted");
      onChanged?.({ id: taskId, _deleted: true });
      onClose?.();
    } catch { toast.error("Delete failed"); }
  };

  const subs = task?.subtasks || [];
  const doneSubs = subs.filter((s) => s.done).length;
  const pct = subs.length ? Math.round((doneSubs / subs.length) * 100) : 0;

  return (
    <Sheet open={!!open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl p-0 overflow-y-auto"
        data-testid="task-drawer"
      >
        <SheetTitle className="sr-only">{task?.title || "Task details"}</SheetTitle>
        {loading || !task ? (
          <div className="h-full flex items-center justify-center text-muted-foreground gap-2 py-24">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading task…
          </div>
        ) : (
          <div className="pb-16">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border px-6 pt-6 pb-4">
              <div className="flex items-start gap-3">
                <button
                  onClick={() => {
                    const pending = (task.subtasks || []).some((s) => !s.done);
                    if (task.status !== "done" && pending) {
                      toast.info("Tick off the subtasks first — the task completes itself when they're all done");
                      return;
                    }
                    patch({ status: task.status === "done" ? "todo" : "done" }, { silent: true });
                  }}
                  data-testid="drawer-toggle-status"
                  className="mt-1 shrink-0"
                >
                  {task.status === "done"
                    ? <CheckCircle2 className="w-5 h-5 text-[hsl(var(--halo-teal))]" />
                    : <Circle className="w-5 h-5 text-muted-foreground/50 hover:text-[hsl(var(--studio-primary))]" />}
                </button>
                <input
                  value={task.title || ""}
                  onChange={(e) => patchDebounced({ title: e.target.value })}
                  data-testid="drawer-title-input"
                  className={cn(
                    "flex-1 bg-transparent text-xl font-semibold tracking-tight outline-none border-b border-transparent focus:border-border pb-1 pr-6",
                    task.status === "done" && "line-through text-muted-foreground"
                  )}
                />
              </div>
              <div className="mt-2 flex items-center gap-3 text-[11px] text-muted-foreground pl-8">
                <span>{saving ? "Saving…" : "All changes saved"}</span>
                {subs.length > 0 && <span>· {doneSubs}/{subs.length} subtasks</span>}
              </div>
            </div>

            <div className="px-6 space-y-7 pt-6">
              {/* Meta */}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Status">
                  <Select value={task.status} onValueChange={(v) => patch({ status: v }, { silent: true })}>
                    <SelectTrigger data-testid="drawer-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Priority">
                  <Select value={task.priority} onValueChange={(v) => patch({ priority: v }, { silent: true })}>
                    <SelectTrigger data-testid="drawer-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIO.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Due date">
                  <Input type="date" value={task.due_date || ""} data-testid="drawer-due-date"
                    onChange={(e) => patch({ due_date: e.target.value || null }, { silent: true })} />
                </Field>
                <Field label="Duration (min)">
                  <Input type="number" min={5} step={5} value={task.duration_minutes ?? 30} data-testid="drawer-duration"
                    onChange={(e) => patchDebounced({ duration_minutes: parseInt(e.target.value) || 30 })} />
                </Field>
                {stages.length > 0 && (
                  <Field label="Stage">
                    <Select
                      value={task.stage_key || "__none__"}
                      onValueChange={(v) => patch({ stage_key: v === "__none__" ? null : v }, { silent: true })}
                    >
                      <SelectTrigger data-testid="drawer-stage"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Unassigned</SelectItem>
                        {stages.map((s) => <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                )}
                <Field label="Scheduled for">
                  <Input
                    type="datetime-local"
                    value={task.scheduled_for ? task.scheduled_for.slice(0, 16) : ""}
                    data-testid="drawer-scheduled-for"
                    onChange={(e) => patch({ scheduled_for: e.target.value ? new Date(e.target.value).toISOString() : null }, { silent: true })}
                  />
                </Field>
                <Field label="Repeats">
                  <Select
                    value={task.is_recurring ? (task.recurrence_rule || "weekly") : "none"}
                    onValueChange={(v) => patch(
                      v === "none"
                        ? { is_recurring: false, recurrence_rule: null }
                        : { is_recurring: true, recurrence_rule: v },
                      { silent: true }
                    )}
                  >
                    <SelectTrigger data-testid="drawer-recurrence"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Doesn't repeat</SelectItem>
                      <SelectItem value="daily">Every day</SelectItem>
                      <SelectItem value="weekly">Every week</SelectItem>
                      <SelectItem value="fortnightly">Every fortnight</SelectItem>
                      <SelectItem value="monthly">Every month</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Schedule window">
                  <Select
                    value={task.schedule_window || "work_hours"}
                    onValueChange={(v) => patch({ schedule_window: v }, { silent: true })}
                  >
                    <SelectTrigger data-testid="drawer-schedule-window"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(settings?.schedule_windows || []).map((w) => (
                        <SelectItem key={w.key} value={w.key}>{w.name} · {w.start}–{w.end}</SelectItem>
                      ))}
                      <SelectItem value="custom">Specific time (custom)</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Assignee">
                  <Select
                    value={task.assignee_person_id || "__none__"}
                    onValueChange={(v) => patch({ assignee_person_id: v === "__none__" ? null : v }, { silent: true })}
                  >
                    <SelectTrigger data-testid="drawer-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Unassigned</SelectItem>
                      {people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <div className="col-span-2 flex items-center gap-2 pt-1">
                  <Switch checked={!!task.is_pinned} data-testid="drawer-pin"
                    onCheckedChange={(v) => patch({ is_pinned: v }, { silent: true })} />
                  <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Pin className="w-3 h-3" /> Pin to top of lists</span>
                </div>
                <div className="col-span-2 flex items-center gap-2">
                  <Switch checked={!!task.block_calendar} data-testid="drawer-block-calendar"
                    onCheckedChange={(v) => patch({ block_calendar: v }, { silent: true })} />
                  <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                    <CalendarClock className="w-3 h-3" /> Block this time on my calendar
                    {settings?.google_connected ? " (and Google)" : ""}
                  </span>
                </div>
              </div>

              {/* Description */}
              <Section icon={ListChecks} title="Description">
                <Textarea
                  rows={5}
                  placeholder="Add detail, links, direction notes…"
                  value={task.description || ""}
                  data-testid="drawer-description"
                  onChange={(e) => patchDebounced({ description: e.target.value })}
                  className="resize-y"
                />
              </Section>

              {/* Subtasks */}
              <Section icon={ListChecks} title={`Subtasks ${subs.length ? `(${doneSubs}/${subs.length})` : ""}`}>
                {subs.length > 0 && (
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden mb-3" data-testid="drawer-subtask-progress">
                    <div className="h-full bg-[hsl(var(--halo-teal))] transition-[width]" style={{ width: `${pct}%` }} />
                  </div>
                )}
                <div className="space-y-1.5">
                  {subs.map((st) => (
                    <div key={st.id} className="group flex items-center gap-2 rounded-lg border border-border px-2.5 py-2" data-testid={`drawer-subtask-${st.id}`}>
                      <button onClick={() => toggleSubtask(st)} data-testid={`drawer-subtask-toggle-${st.id}`}>
                        {st.done
                          ? <CheckCircle2 className="w-4 h-4 text-[hsl(var(--halo-teal))]" />
                          : <Circle className="w-4 h-4 text-muted-foreground/50" />}
                      </button>
                      <span className={cn("text-sm flex-1", st.done && "line-through text-muted-foreground")}>{st.title}</span>
                      <button onClick={() => removeSubtask(st)} data-testid={`drawer-subtask-delete-${st.id}`}
                        className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-[hsl(var(--studio-danger))]">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <Input
                    placeholder="Add a subtask and press Enter"
                    value={newSub}
                    data-testid="drawer-subtask-input"
                    onChange={(e) => setNewSub(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSubtask(); } }}
                  />
                  <Button variant="outline" size="icon" onClick={addSubtask} data-testid="drawer-subtask-add">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </Section>

              {/* Attachments */}
              <Section icon={Paperclip} title={`Attachments ${files.length ? `(${files.length})` : ""}`}>
                <FileDropZone
                  context={{ task_id: taskId, project_id: task.project_id || undefined }}
                  compact
                  label="Drop files onto this task"
                  onUploaded={(rec) => setFiles((prev) => [rec, ...prev])}
                  data-testid="drawer-dropzone"
                />
                <div className="space-y-1.5 mt-2">
                  {files.map((f) => (
                    <div key={f.id} className="flex items-center gap-2 rounded-lg border border-border px-2.5 py-2" data-testid={`drawer-file-${f.id}`}>
                      <Paperclip className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{f.original_filename}</div>
                      <div className="text-[11px] text-muted-foreground">{humanBytes(f.size)}</div>
                    </div>
                    <Button variant="ghost" size="sm" data-testid={`drawer-file-preview-${f.id}`} onClick={() => setPreviewFile(f)}>
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                      <Button variant="ghost" size="sm" onClick={async () => {
                        try {
                          const url = await fileBlobUrl(f.id);
                          const a = document.createElement("a");
                          a.href = url; a.download = f.original_filename; a.click();
                          setTimeout(() => URL.revokeObjectURL(url), 5000);
                        } catch { toast.error("Download failed"); }
                      }}><Download className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="sm" data-testid={`drawer-file-delete-${f.id}`} onClick={async () => {
                        await deleteFile(f.id);
                        setFiles((prev) => prev.filter((x) => x.id !== f.id));
                      }}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  ))}
                </div>
              </Section>

              {/* Comments */}
              <Section icon={MessageSquare} title={`Comments ${(task.comments || []).length ? `(${task.comments.length})` : ""}`}>
                <div className="space-y-2">
                  {(task.comments || []).map((c) => (
                    <div key={c.id} className="group rounded-lg border border-border p-3" data-testid={`drawer-comment-${c.id}`}>
                      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                        <span className="font-medium text-foreground">{c.author_name || "You"}</span>
                        <span>{new Date(c.created_at).toLocaleString()}</span>
                        <button onClick={() => removeComment(c)} data-testid={`drawer-comment-delete-${c.id}`}
                          className="ml-auto opacity-0 group-hover:opacity-100 hover:text-[hsl(var(--studio-danger))]">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="text-sm mt-1.5 whitespace-pre-wrap">{c.body}</div>
                    </div>
                  ))}
                  {(task.comments || []).length === 0 && (
                    <div className="text-sm text-muted-foreground py-3">No comments yet — leave a note for future you.</div>
                  )}
                </div>
                <div className="flex gap-2 mt-2">
                  <Textarea
                    rows={2}
                    placeholder="Write a comment…"
                    value={newComment}
                    data-testid="drawer-comment-input"
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); addComment(); } }}
                  />
                  <Button variant="outline" size="icon" onClick={addComment} data-testid="drawer-comment-add">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </Section>

              {/* Activity log */}
              <Section icon={History} title="Activity">
                {(task.activity || []).length === 0 ? (
                  <div className="text-sm text-muted-foreground py-2">No changes recorded yet.</div>
                ) : (
                  <div className="space-y-0" data-testid="drawer-activity-log">
                    {[...task.activity].reverse().map((a) => (
                      <div key={a.id} className="flex gap-3 text-xs py-2 border-l border-border pl-3 ml-1" data-testid={`drawer-activity-${a.id}`}>
                        <div className="flex-1">
                          <span className="text-muted-foreground">{a.label} → </span>
                          <span className="font-medium">{formatActivityValue(a.field, a.to_value, stages, people)}</span>
                          {a.from_value && (
                            <span className="text-muted-foreground"> (was {formatActivityValue(a.field, a.from_value, stages, people)})</span>
                          )}
                        </div>
                        <span className="text-muted-foreground whitespace-nowrap">{new Date(a.at).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Section>

              <div className="pt-2 border-t border-border flex flex-wrap items-center gap-2">
                {task.is_recurring && (
                  <Button
                    variant="outline"
                    data-testid="drawer-skip-occurrence"
                    onClick={async () => {
                      try {
                        const { data } = await api.post(`/tasks/${taskId}/skip-occurrence`);
                        setTask(data); onChanged?.(data);
                        toast.success("Skipped this one — series intact");
                      } catch { toast.error("Could not skip"); }
                    }}
                  >
                    <SkipForward className="w-4 h-4 mr-2" /> Skip this occurrence
                  </Button>
                )}
                <Button variant="ghost" onClick={removeTask} data-testid="drawer-delete-task"
                  className="text-[hsl(var(--studio-danger))] hover:text-[hsl(var(--studio-danger))]">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete task
                </Button>
              </div>
            </div>
          </div>
        )}
        <FilePreviewDialog file={previewFile} open={!!previewFile} onClose={() => setPreviewFile(null)} />
      </SheetContent>
    </Sheet>
  );
}

function formatActivityValue(field, value, stages, people) {
  if (!value) return "cleared";
  if (field === "stage_key") return stages.find((s) => s.key === value)?.name || value;
  if (field === "assignee_person_id") return people.find((p) => p.id === value)?.name || "someone";
  if (field === "scheduled_for") return new Date(value).toLocaleString();
  return String(value).replace("_", " ");
}

const Field = ({ label, children }) => (  <div className="space-y-1.5">
    <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const Section = ({ icon: Icon, title, children }) => (
  <div>
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground mb-2">
      <Icon className="w-3.5 h-3.5" /> {title}
    </div>
    {children}
  </div>
);
